import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
} from "react-router-dom";
import { useAuthCache } from "@/hooks/useAuthCache";
import { OneSignalProvider } from "@/components/OneSignalProvider";
import { useAuthStore } from "@/stores/authStore";
import { getApiErrorStatus } from "@/lib/api";
import type { PermissionRequirement } from "@/constants/permissions";
import { consoleRoutePermissionRequirements } from "@/constants/consoleNavigation";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ThemeProvider } from "@/design-system/providers/ThemeProvider";
import { MobileViewportDialog } from "@/components/layout/MobileViewportDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { AccessDenied } from "@/components/AccessDenied";
import { Button } from "@/components/ui/button";
import Index from "./pages/Index";
import Home from "./pages/Home";
import Dashboard from "./pages/Dashboard";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Consultation from "./pages/Consultation";
import { TrackerDashboard } from "./components/TrackerDashboard";
import { SidebarLayout } from "./components/SidebarLayout";
import TreatmentOverview from "./pages/TreatmentOverview";
import Patients from "./pages/Patients";
import CreatePatient from "./pages/CreatePatient";
import Notifications from "./pages/Notifications";
import {
  AuthenticatedRoute,
  PermissionRoute,
} from "./components/PermissionRoute";

const LegacyPatientsRedirect = () => {
  const { subsection, patientId, tab } = useParams<{
    subsection?: string;
    patientId?: string;
    tab?: string;
  }>();

  if (subsection === "create-users") {
    return <Navigate to="/patients/create" replace />;
  }

  if (patientId && tab) {
    return <Navigate to={`/patients/${patientId}/${tab}`} replace />;
  }

  if (patientId) {
    return <Navigate to={`/patients/${patientId}`} replace />;
  }

  return <Navigate to="/patients" replace />;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global defaults for queries
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors except 408, 429
        if (error?.status >= 400 && error?.status < 500) {
          return error?.status === 408 || error?.status === 429;
        }
        // Retry up to 3 times for other errors
        return failureCount < 3;
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
    },
    mutations: {
      // Global defaults for mutations
      retry: false,
      onError: (error: any) => {
        console.error("Mutation error:", error);
        // You can add global error handling here
        // For example, show a toast notification
      },
    },
  },
});

const protectedPage = (
  page: ReactNode,
  requirement: PermissionRequirement,
  deniedTo?: string
) => {
  return (
    <PermissionRoute requirement={requirement} deniedTo={deniedTo}>
      {page}
    </PermissionRoute>
  );
};

const AppRoutes = () => {
  useAuthCache(); // Handle auth cache clearing

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Index />} />
        {/* All sidebar-enabled routes share a single SidebarLayout */}
        <Route element={<SidebarLayout />}>
          {/* Home Dashboard */}
          <Route
            path="/home"
            element={protectedPage(
              <Home />,
              consoleRoutePermissionRequirements.home,
              "/"
            )}
          />
          {/* Pings Dashboard routes with chat support */}
          <Route
            path="/dashboard"
            element={protectedPage(
              <Dashboard />,
              consoleRoutePermissionRequirements.pings
            )}
          />
          <Route
            path="/dashboard/chat"
            element={protectedPage(
              <Dashboard />,
              consoleRoutePermissionRequirements.pings
            )}
          />
          <Route
            path="/dashboard/chat/:chatId"
            element={protectedPage(
              <Dashboard />,
              consoleRoutePermissionRequirements.pings
            )}
          />
          <Route
            path="/dashboard/internal-chat"
            element={protectedPage(
              <Dashboard />,
              consoleRoutePermissionRequirements.pings
            )}
          />
          <Route
            path="/dashboard/internal-chat/:chatId"
            element={protectedPage(
              <Dashboard />,
              consoleRoutePermissionRequirements.pings
            )}
          />
          {/* Tracker Dashboard */}
          <Route
            path="/tracker"
            element={protectedPage(
              <TrackerDashboard />,
              consoleRoutePermissionRequirements.tracker
            )}
          />
          {/* Treatment Overview (admin flow) */}
          <Route
            path="/treatment-overview"
            element={protectedPage(
              <TreatmentOverview />,
              consoleRoutePermissionRequirements.treatmentOverview
            )}
          />
          {/* Patients */}
          <Route
            path="/patients"
            element={protectedPage(
              <Patients />,
              consoleRoutePermissionRequirements.patients
            )}
          />
          <Route
            path="/patients/create"
            element={protectedPage(
              <CreatePatient />,
              consoleRoutePermissionRequirements.createPatient
            )}
          />
          <Route
            path="/patients/:patientId/:tab"
            element={protectedPage(
              <Patients />,
              consoleRoutePermissionRequirements.patients
            )}
          />
          <Route
            path="/patients/:patientId"
            element={protectedPage(
              <Patients />,
              consoleRoutePermissionRequirements.patients
            )}
          />

          {/* Every authenticated provider has notifications. Which CATEGORIES
              they see is gated per-category inside the page, not at the route —
              gating the whole page on one module's permission would hide
              appointment notifications from someone who lacks chat access. */}
          <Route path="/notifications" element={<Notifications />} />

          {/* Legacy patient settings URLs */}
          <Route
            path="/settings/patients/:subsection/:patientId/:tab"
            element={<LegacyPatientsRedirect />}
          />
          <Route
            path="/settings/patients/:subsection/:patientId"
            element={<LegacyPatientsRedirect />}
          />
          <Route
            path="/settings/patients/:subsection"
            element={<LegacyPatientsRedirect />}
          />

          {/* Settings Routes */}
          <Route
            path="/settings/:section/:subsection/:patientId/:tab"
            element={protectedPage(
              <Settings />,
              consoleRoutePermissionRequirements.settings
            )}
          />
          <Route
            path="/settings/:section/:subsection/:patientId"
            element={protectedPage(
              <Settings />,
              consoleRoutePermissionRequirements.settings
            )}
          />
          <Route
            path="/settings/:section"
            element={protectedPage(
              <Settings />,
              consoleRoutePermissionRequirements.settings
            )}
          />
          <Route
            path="/settings/:section/:subsection"
            element={protectedPage(
              <Settings />,
              consoleRoutePermissionRequirements.settings
            )}
          />
        </Route>
        {/* Consultation - separate layout (no sidebar) */}
        <Route
          path="/consultation/:appointmentId"
          element={
            <AuthenticatedRoute>
              <Consultation />
            </AuthenticatedRoute>
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
};

const AppContent = () => {
  const isMobile = useIsMobile();
  const { clearAuth, refreshCurrentUser, setLoading } = useAuthStore();
  const [isBootstrappingAuth, setIsBootstrappingAuth] = useState(() =>
    Boolean(localStorage.getItem("access_token"))
  );
  const [hasBootstrapError, setHasBootstrapError] = useState(false);

  const bootstrapAuth = useCallback(async () => {
    const hasAccessToken = Boolean(localStorage.getItem("access_token"));

    if (!hasAccessToken) {
      clearAuth();
      setLoading(false);
      setIsBootstrappingAuth(false);
      return;
    }

    setHasBootstrapError(false);
    setIsBootstrappingAuth(true);
    try {
      await refreshCurrentUser();
    } catch (error: unknown) {
      const status = getApiErrorStatus(error);

      if (status === 401 || status === 403) {
        clearAuth();
      } else if (localStorage.getItem("access_token")) {
        setHasBootstrapError(true);
      }
    } finally {
      setIsBootstrappingAuth(false);
    }
  }, [clearAuth, refreshCurrentUser, setLoading]);

  useEffect(() => {
    void bootstrapAuth();
  }, [bootstrapAuth]);

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      {isMobile ? (
        <MobileViewportDialog />
      ) : isBootstrappingAuth ? null : hasBootstrapError ? (
        <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background p-4">
          <AccessDenied description="We could not verify your current permissions. Retry to securely restore your Console session." />
          <Button onClick={() => void bootstrapAuth()}>
            Retry permission check
          </Button>
        </div>
      ) : (
        <AppRoutes />
      )}
    </TooltipProvider>
  );
};

const App = () => (
  <ThemeProvider defaultTheme="light" disableStorage={true}>
    <QueryClientProvider client={queryClient}>
      <OneSignalProvider>
        <AppContent />
      </OneSignalProvider>
    </QueryClientProvider>
  </ThemeProvider>
);

export default App;
